import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";

/**
 * Visible FAQ section. The JSON-LD FAQPage schema is added separately
 * via the SEO component (see faqSchema() helper).
 *
 * Props:
 *  - faqs: [{ q: string, a: string }]
 *  - title?: string (defaults to "Ofte stillede spørgsmål")
 *  - subtitle?: string
 *  - testId?: string
 */
export default function FAQSection({
  faqs,
  title = "Ofte stillede spørgsmål",
  subtitle,
  testId = "faq-section",
}) {
  if (!faqs || faqs.length === 0) return null;

  return (
    <section
      className="my-12 md:my-16"
      data-testid={testId}
      aria-labelledby={`${testId}-heading`}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-secondary/15 flex items-center justify-center">
          <HelpCircle className="h-5 w-5 text-secondary" />
        </div>
        <div>
          <h2
            id={`${testId}-heading`}
            className="font-heading text-2xl md:text-3xl font-bold text-primary"
          >
            {title}
          </h2>
          {subtitle && (
            <p className="text-muted-foreground text-sm mt-1">{subtitle}</p>
          )}
        </div>
      </div>

      <Accordion type="single" collapsible className="w-full">
        {faqs.map((item, i) => (
          <AccordionItem
            key={i}
            value={`faq-${i}`}
            className="border-border/60"
            data-testid={`${testId}-item-${i}`}
          >
            <AccordionTrigger className="text-left text-base md:text-lg font-medium text-primary hover:no-underline py-4">
              {item.q}
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed text-sm md:text-base pb-4">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

/**
 * Generelle FAQs der vises på forsiden og alle blogindlæg.
 * Genbruges via import: `import { GENERAL_FAQS } from "@/components/FAQSection"`
 */
export const GENERAL_FAQS = [
  {
    q: "Kan AI skrive en personlig festtale?",
    a: "Ja — MinFesttale bruger AI trænet specifikt til danske festtaler. Du fortæller dine egne historier og minder via en præcist udformet formular, og AI'en former dem til en personlig tale der lyder som dig.",
  },
  {
    q: "Hvad koster det at få skrevet en festtale?",
    a: "Hos MinFesttale koster en festtale fra 149 kr. afhængigt af længde. Du ser et gratis preview inden du betaler.",
  },
  {
    q: "Hvordan er MinFesttale forskellig fra ChatGPT?",
    a: "MinFesttale er AI trænet specifikt til danske festtaler af mennesker med erfaring i taleskriving. Du udfylder en præcis formular med dine egne historier — resultatet er en personlig tale på professionelt niveau, ikke en generisk skabelon.",
  },
  {
    q: "Kan jeg redigere talen efter jeg har betalt?",
    a: "Ja — efter betaling kan du redigere talen frit i vores program indtil den er perfekt, og downloade den som et flot PDF-dokument klar til at printe og holde.",
  },
  {
    q: "Hvad sker der hvis jeg ikke er tilfreds med talen?",
    a: "MinFesttale tilbyder 100% tilfredshedsgaranti. Hvis du ikke er tilfreds kan du generere en ny version gratis inden for 24 timer.",
  },
];
